import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { gr } from "../tokens";

const optionOf = (o) =>
  typeof o === "object" && o !== null
    ? { value: String(o.value), label: o.label ?? String(o.value) }
    : { value: String(o), label: String(o) };

// Answer chips for form questions (not filters: use GrFilterChips for those).
// One pick (radio) or several (`multiple`, checkbox). Picking the chosen one again clears it.
// options = ["Great", "Good"] or [{ value, label }]; value = string, or string[] with `multiple`.
export default function GrChoiceChips({
  options,
  value,
  onChange,
  multiple = false,
  error = false,
  ariaLabel,
  sx,
}) {
  const picked = multiple
    ? (Array.isArray(value) ? value : []).map(String)
    : value == null || value === ""
      ? []
      : [String(value)];
  const toggle = (v) => {
    if (!multiple) return onChange(picked[0] === v ? "" : v);
    return onChange(
      picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]
    );
  };
  return (
    <Box
      role={multiple ? "group" : "radiogroup"}
      aria-label={ariaLabel}
      aria-invalid={error || undefined}
      sx={{ display: "flex", flexWrap: "wrap", gap: gr.space[2], ...sx }}
    >
      {options.map(optionOf).map((o) => {
        const on = picked.includes(o.value);
        return (
          <ButtonBase
            key={o.value}
            disableRipple
            role={multiple ? "checkbox" : "radio"}
            aria-checked={on}
            onClick={() => toggle(o.value)}
            sx={{
              minHeight: gr.control.touch,
              px: gr.space[4],
              borderRadius: gr.radius.control,
              border: `1px solid ${
                on ? gr.color.brand : error ? gr.color.danger : gr.color.line
              }`,
              background: on ? gr.color.brandSoft : gr.color.surface,
              color: on ? gr.color.brandDark : gr.color.ink,
              fontFamily: gr.font.family,
              fontSize: gr.font.size.md,
              fontWeight: on ? gr.font.weight.semibold : gr.font.weight.medium,
              transition: "background-color .15s ease, border-color .15s ease",
              "&:hover": { borderColor: on ? gr.color.brand : gr.color.lineStrong },
              "&.Mui-focusVisible": {
                boxShadow: gr.shadow.focus,
                borderColor: gr.color.brand,
              },
            }}
          >
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

GrChoiceChips.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.node,
      }),
    ])
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  error: PropTypes.bool,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};
